import configs from '../config';

const API_BASE = `${configs.BACKEND_URL}/api`;
const CACHE_TTL = 5000;

const DEFAULT_ONBOARDING_PREFS = {
  completed: false,
  skipped: false,
  skippedUntil: null,
};

const stepsCache = new Map();
const inFlightRequests = new Map();

/**
 * Check if job filters have been configured
 */
export const hasConfiguredJobFilters = (criteria = {}) => {
  if (!criteria || typeof criteria !== 'object') return false;

  if ((criteria.searchTerms || []).length > 0) return true;
  if ((criteria.skills || []).length > 0) return true;
  if ((criteria.locations || []).length > 0) return true;
  if (criteria.category) return true;
  if (criteria.jobType && criteria.jobType !== 'ANY') return true;
  if (criteria.experienceLevel && criteria.experienceLevel !== 'ANY') return true;
  if (criteria.dateRange && criteria.dateRange !== 'ANY') return true;
  if (criteria.verifiedPaymentOnly) return true;
  if (criteria.previousClients) return true;
  if (criteria.clientHiresMin != null) return true;
  if (criteria.clientFeedbackMin != null) return true;
  if (criteria.budgetRange?.rangeStart != null || criteria.budgetRange?.rangeEnd != null) return true;
  if (criteria.hourlyRate?.rangeStart != null || criteria.hourlyRate?.rangeEnd != null) return true;
  if (criteria.proposalRange?.rangeStart != null || criteria.proposalRange?.rangeEnd != null) return true;

  return false;
};

const buildStepsCacheKey = (user, permissions = {}, tenantId = null) => {
  const {
    canUpdateTenant = false,
    canUpdateFilters = false,
    canReadKnowledge = false,
    canInvite = false,
    canReadProposalData = false,
  } = permissions;

  return [
    tenantId || user?.tenantId || 'none',
    user?._id || 'anonymous',
    user?.role || 'none',
    canUpdateTenant ? '1' : '0',
    canUpdateFilters ? '1' : '0',
    canReadKnowledge ? '1' : '0',
    canInvite ? '1' : '0',
    canReadProposalData ? '1' : '0',
  ].join(':');
};

const extractSyncData = (syncPayload) => {
  if (!syncPayload) return null;
  if (syncPayload.success && syncPayload.data) return syncPayload.data;
  return syncPayload;
};

/**
 * Determine onboarding flow variant based on tenant configuration and user role
 * @param {Object} tenantConfig - Tenant configuration response
 * @param {Object} user - Current user object
 * @returns {string} - 'solo' or 'agency'
 */
export const determineOnboardingVariant = (tenantConfig, user) => {
  // Check if variant is explicitly set in tenant metadata
  const explicitVariant = tenantConfig?.data?.metadata?.onboardingVariant;
  if (explicitVariant === 'solo' || explicitVariant === 'agency') {
    return explicitVariant;
  }

  // Default to 'solo' for now, can be extended based on other factors
  // Future: could check team size, subscription tier, etc.
  return 'solo';
};

/**
 * Build onboarding steps with completion status
 */
export const buildOnboardingSteps = async (fetchWithTenant, user, permissions, options = {}) => {
  const { tenantId = null, forceRefresh = false } = options;
  const cacheKey = buildStepsCacheKey(user, permissions, tenantId);
  const now = Date.now();
  const cached = stepsCache.get(cacheKey);

  if (!forceRefresh && cached && now < cached.expiry) {
    return cached.result;
  }

  if (inFlightRequests.has(cacheKey)) {
    return inFlightRequests.get(cacheKey);
  }

  const request = buildOnboardingStepsInner(fetchWithTenant, user, permissions)
    .then((result) => {
      stepsCache.set(cacheKey, {
        result,
        expiry: Date.now() + CACHE_TTL,
      });
      inFlightRequests.delete(cacheKey);
      return result;
    })
    .catch((error) => {
      inFlightRequests.delete(cacheKey);
      throw error;
    });

  inFlightRequests.set(cacheKey, request);
  return request;
};

const buildOnboardingStepsInner = async (fetchWithTenant, user, permissions) => {
  const {
    canUpdateTenant,
    canUpdateFilters,
    canReadKnowledge,
    canInvite,
    canReadProposalData,
  } = permissions;

  const fetchJson = async (path) => {
    try {
      const response = await fetchWithTenant(`${API_BASE}${path}`);
      if (!response.ok) return null;
      return response.json();
    } catch {
      return null;
    }
  };

  const [
    oauthStatus,
    tenantConfig,
    invitations,
    appProposals,
    extensionProposals,
    vectorData,
    portfolio,
    syncStatus,
    profileImportStatus,
  ] = await Promise.all([
    fetchJson('/upwork/oauth/status'),
    fetchJson('/tenant/config'),
    fetchJson('/user/invitations'),
    fetchJson('/proposals?source=app&mine=true&page=1&limit=1'),
    fetchJson('/proposals?source=extension&mine=true&page=1&limit=1'),
    fetchJson('/vector-data?page=1&limit=1&type=about,services,testimonials,skills,past_projects'),
    fetchJson('/portfolio/all?page=1&limit=1'),
    fetchJson('/tenant/sync-status'),
    fetchJson('/profile-import/status'),
  ]);

  const oauthConnected = oauthStatus?.data?.status === 'connected';
  const profileImportReady = profileImportStatus?.data?.ready === true;
  const accessEnabled = oauthConnected || profileImportReady;
  const browserAssistMode = profileImportReady && !oauthConnected;
  const criteria = tenantConfig?.data?.jobFilters?.criteria || {};
  const filtersConfigured = hasConfiguredJobFilters(criteria);
  const syncData = extractSyncData(syncStatus);
  const jobsSyncLastTime = syncData?.jobs?.lastSyncTime;
  const jobsSyncError = syncData?.jobs?.lastError;
  const jobsSynced = filtersConfigured && Boolean(jobsSyncLastTime) && !jobsSyncError;

  const currentUserEmail = (user?.email || '').trim().toLowerCase();
  const teammateInvited = (invitations?.invitations || []).some((invitation) => {
    const invitedEmail = (invitation?.email || '').trim().toLowerCase();
    if (!invitedEmail) return false;
    return invitedEmail !== currentUserEmail;
  });

  const appProposalsTotal = Number(appProposals?.pagination?.total || 0);
  const extensionProposalsTotal = Number(extensionProposals?.pagination?.total || 0);
  const firstProposalCreated = appProposalsTotal + extensionProposalsTotal > 0;
  const vectorDataTotal = Number(vectorData?.total || 0);
  const portfolioTotal = Number(portfolio?.pagination?.total || 0);
  const aiKnowledgeAdded = vectorDataTotal > 0 || portfolioTotal > 0;

  // Determine onboarding flow variant
  const flowVariant = determineOnboardingVariant(tenantConfig, user);

  // Define all steps with variant tags
  const allSteps = [
    {
      id: 'connect_upwork',
      title: 'Enable Upwork Access',
      description: 'Use OAuth for full sync or import your Upwork profile pages for Browser-Assist mode.',
      path: '/admin/integrations',
      target: 'connect-upwork-button',
      completed: accessEnabled,
      enabled: canUpdateTenant,
      variants: ['solo', 'agency'],
    },
    {
      id: 'job_filters',
      title: 'Set Job Filters',
      description: 'Propel can suggest and apply your first filters automatically.',
      path: '/admin/job-filters',
      target: 'save-job-filters-button',
      completed: filtersConfigured,
      enabled: canUpdateFilters,
      variants: ['solo', 'agency'],
    },
    {
      id: 'sync_jobs',
      title: browserAssistMode ? 'Finish Browser-Assist Setup' : 'Sync Your First Jobs',
      description: browserAssistMode
        ? 'Import your profile pages in the extension so Propel can qualify jobs and draft proposals on Upwork.'
        : 'Propel can pull in your first matching jobs as soon as access is live.',
      path: browserAssistMode ? '/admin/integrations' : '/jobs',
      target: browserAssistMode ? 'browser-assist-import-card' : 'sync-jobs-now-button',
      completed: browserAssistMode ? profileImportReady : jobsSynced,
      enabled: browserAssistMode ? canUpdateTenant : canUpdateFilters,
      variants: ['solo', 'agency'],
    },
    {
      id: 'ai_knowledge',
      title: 'Add AI Knowledge',
      description: 'Propel syncs your Upwork profile into AI knowledge automatically.',
      path: '/admin/vector-data',
      target: 'add-ai-knowledge-button',
      completed: aiKnowledgeAdded,
      enabled: canReadKnowledge,
      variants: ['solo', 'agency'],
    },
    {
      id: 'invite_teammate',
      title: 'Invite a Teammate',
      description: 'Invite at least one teammate to collaborate.',
      path: '/admin/users',
      target: 'invite-user-button',
      completed: teammateInvited,
      enabled: canInvite,
      variants: ['agency'], // Only shown for agency variant
    },
    {
      id: 'first_proposal',
      title: 'Create First Proposal',
      description: 'Propel can prepare a first draft from a strong early match.',
      path: '/jobs',
      target: 'jobs-search',
      completed: firstProposalCreated,
      enabled: canReadProposalData,
      variants: ['solo', 'agency'],
    },
  ];

  // Filter steps by variant and enabled status
  const steps = allSteps
    .filter((step) => step.enabled && step.variants.includes(flowVariant))
    .map((step) => {
      // Remove variants field from returned steps (internal only)
      const { variants, ...stepWithoutVariants } = step;
      return stepWithoutVariants;
    });

  return { steps, flowVariant };
};

/**
 * Build storage key for onboarding preferences
 */
export const buildOnboardingStorageKey = (userId, tenantId) =>
  `onboarding-prefs:${userId || 'none'}:${tenantId || 'none'}`;

/**
 * Get onboarding preferences from localStorage
 */
export const getOnboardingPrefs = (userId, tenantId) => {
  if (!userId) return { ...DEFAULT_ONBOARDING_PREFS };

  const key = buildOnboardingStorageKey(userId, tenantId);

  try {
    const stored = localStorage.getItem(key);
    if (!stored) return { ...DEFAULT_ONBOARDING_PREFS };
    const parsed = JSON.parse(stored);
    return { ...DEFAULT_ONBOARDING_PREFS, ...(parsed || {}) };
  } catch {
    return { ...DEFAULT_ONBOARDING_PREFS };
  }
};

/**
 * Save onboarding preferences to localStorage
 */
export const saveOnboardingPrefs = (userId, tenantId, prefs) => {
  if (!userId) return;
  const key = buildOnboardingStorageKey(userId, tenantId);
  localStorage.setItem(key, JSON.stringify(prefs || {}));
};

/**
 * Clear onboarding step cache (for tests/manual refresh)
 */
export const clearOnboardingStepsCache = () => {
  stepsCache.clear();
  inFlightRequests.clear();
};

/**
 * Check if user is eligible for onboarding (admin or superuser)
 */
export const isOnboardingEligible = (user) => user?.role === 'admin' || user?.role === 'superuser';

/**
 * Check if onboarding is complete (all steps done OR user marked as complete/skipped)
 */
export const isOnboardingComplete = (steps, prefs) => {
  if (prefs?.completed) return true;

  if (prefs?.skipped && prefs?.skippedUntil) {
    const skippedUntil = new Date(prefs.skippedUntil);
    if (!Number.isNaN(skippedUntil.getTime()) && skippedUntil > new Date()) {
      return true;
    }
  }

  return steps.length > 0 && steps.every((step) => step.completed);
};
